import { isNullOrUndefined } from 'src/app/shared/utils';

/**
 * Compares two arrays.
 * This method treats empty arrays, null and undefined as equal.
 * This method treats null and undefined values are equal.
 * This method considers values with different types as inequal.
 * This method compares values of the same type using the '==' operator.
 * The method will recursively compare arrays  that are elements of the provided arrays. */
export function compareArraysRecursive(a: any[], b: any[]) {
  let d = (a?.length ?? 0) - (b?.length ?? 0);
  if (d != 0)
    return d;

  for (let i = 0; i < a.length; ++i) {
    let av = a[i];
    let bv = b[i];

    // Same reference
    if (av === bv)
      continue;

    if (isNullOrUndefined(av)) {
      if (isNullOrUndefined(bv)) {
        // Both null/undefined
        continue;
      }
      else {
        return -1;
      }
    }
    else if (isNullOrUndefined(bv)) {
      return +1;
    }

    if (Array.isArray(av)) {
      if (Array.isArray(bv)) {
        d = compareArraysRecursive(av, bv);
        if (d != 0)
          return d;
        else
          continue;
      }
    } else {
      d = (typeof av).localeCompare(typeof bv);
      if (d != 0)
        return d;
    }

    if (av == bv)
      continue;

    if (typeof av === 'string') {
      d = av.localeCompare(bv);
      if (d != 0)
        return d;
    }
  }

  return d;
}

export function deepEquals(a: any, b: any): boolean {
  return _deepEquals(a, b, new Set<any>());
}

function _deepEquals(a: any, b: any, visited: Set<any>): boolean {
  if (typeof a != typeof b)
    return false;

  if (a === b)
    return true;

  if (typeof a === 'string') {
    if (typeof b === 'string') {
      return a == b;
    }

    return false;
  }

  if (typeof a === 'number') {
    if (isNaN(a) && isNaN(b))
      return true;

    return a === b;
  }

  if (visited.has(a))
    throw new Error(`Cycle`, { cause: a });

  if (visited.has(b))
    throw new Error(`Cycle`, { cause: b });

  visited.add(a);
  visited.add(b);

  if (Array.isArray(a) || Array.isArray(b)) {
    if (Array.isArray(a) != Array.isArray(b))
      return false;

    if (a.length != b.length)
      return false;

    for (let i = 0; i < a.length; ++i) {
      if (!deepEquals(a[i], b[i]))
        return false;
    }

    return true;
  }

  let ak = Object.keys(a);
  let bk = Object.keys(b);
  let checked = new Set<string>();

  for (let keys of [ak, bk]) {
    for (let k of keys) {
      if (checked.has(k))
        continue;

      checked.add(k);

      let av = (k in a) ? a[k] : undefined;
      let bv = (k in b) ? b[k] : undefined;

      if (!_deepEquals(av, bv, visited))
        return false;
    }
  }

  return true;
}
